import { SentryFetchHttpHandler } from "@amzn/sentry-fetch-http-handler";
import { PiezoWitnessClient, CreateObservationCommand, UpdateObservationCommand, ListIdentityDocumentsCommand, } from '@amzn/piezo-witness-typescript-client';
import { WITNESS_ENDPOINTS, REGION_TO_PARTITION, GAMEDAY_REGIONS } from '../__generated__/piezo-endpoints.js';
/** The default AWS partition to use when no region is provided. */
const DEFAULT_PARTITION = 'aws';
/**
 * Returns the Witness regions (with endpoint URLs) for the given stage/authType, filtered to the
 * partition of the given region. Witness endpoints (including the static beta endpoints, which are
 * not sourced from RIP) are discovered from the generated RIP data: the region's partition is looked
 * up via the generated region-to-partition mapping, then all Witness endpoints in that partition are
 * returned. Gameday/test regions (per RIP's "gameday_region" custom property) are excluded unless
 * they are the requested target region, since they have no real customers and no expected Witness
 * deployment outside of themselves.
 */
export function getWitnessRegions(stage, authType, region) {
    const partition = (region && REGION_TO_PARTITION[region]) || DEFAULT_PARTITION;
    const regionsForPartition = WITNESS_ENDPOINTS[stage]?.[authType]?.[partition] ?? {};
    const output = Object.entries(regionsForPartition)
        .filter(([witnessRegion, endpoints]) => endpoints.length > 0 && (witnessRegion === region || !GAMEDAY_REGIONS.includes(witnessRegion)))
        .map(([witnessRegion, endpoints]) => ({ region: witnessRegion, endpoint: endpoints[0].url }));
    if (output.length < 1) {
        throw new Error(`No witness endpoints found in ${partition} partition`);
    }
    return output;
}
/**
 * Returns a shuffled/random array of witness client configs from the given client configs.
 */
function shuffleClients(clients) {
    const shuffledClients = [...clients];
    for (let i = clients.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffledClients[i], shuffledClients[j]] = [shuffledClients[j], shuffledClients[i]];
    }
    return shuffledClients;
}
export class PiezoWitnessThickClient {
    clients;
    constructor(props) {
        const witnessRegions = getWitnessRegions(props.stage, props.authType, props.region);
        this.clients = witnessRegions.map(({ region, endpoint }) => {
            return {
                client: this.createClient(props.authType, region, endpoint, props.credentials),
                region,
                endpoint,
            };
        });
    }
    /**
     * Calls the Witness CreateObservation API.
     * Tries the preferred region first, then retries with other regions on failure.
     */
    async createObservation(actionInfoDocument) {
        const command = new CreateObservationCommand({
            actionInfoDocument,
        });
        let lastError;
        for (const config of shuffleClients(this.clients)) {
            try {
                const output = await config.client.send(command);
                return {
                    observedActionInfoDocument: output.observedActionInfoDocument,
                    observationDocument: output.observationDocument,
                    observationId: output.observationId,
                    region: config.region,
                };
            }
            catch (err) {
                lastError = err;
                console.warn(`WARNING: CreateObservation failed for region ${config.region}: ${lastError.message}. Trying next region.`);
            }
        }
        throw new Error(`Failed to create observation from all Witness regions: ${lastError?.message}`);
    }
    /**
     * Calls the Witness UpdateObservation API.
     * Signs an action input document that is chained to an existing observation.
     * Tries the preferred region first, then retries with other regions on failure.
     */
    async updateObservation(observationId, actionInputDocument) {
        const command = new UpdateObservationCommand({
            observationId,
            actionInputDocument,
        });
        let lastError;
        for (const config of shuffleClients(this.clients)) {
            try {
                const output = await config.client.send(command);
                return {
                    observedActionInputDocument: output.observedActionInputDocument,
                    region: config.region,
                };
            }
            catch (err) {
                lastError = err;
                console.warn(`WARNING: UpdateObservation failed for region ${config.region}: ${lastError.message}. Trying next region.`);
            }
        }
        throw new Error(`Failed to update observation from all Witness regions: ${lastError?.message}`);
    }
    /**
     * Fetches all Witness identity decks from all available regions.
     * By default, fails immediately if any region fails.
     * Pass `allowPartialFailure: true` to continue fetching from other regions on failure.
     *
     * @param options.allowPartialFailure - If true, returns decks from successful regions even if some fail.
     * @returns A mapping from witness endpoint to their region and identity decks.
     */
    async listIdentityDocuments(options) {
        const allowPartial = options?.allowPartialFailure ?? false;
        if (allowPartial) {
            const results = await Promise.allSettled(this.clients.map(async (config) => {
                const decks = [];
                let nextToken = undefined;
                do {
                    const resp = await config.client.send(new ListIdentityDocumentsCommand({ nextToken }));
                    if (resp.identityDocuments) {
                        for (const doc of resp.identityDocuments) {
                            if (doc.document) {
                                decks.push(doc.document);
                            }
                        }
                    }
                    nextToken = resp.nextToken;
                } while (nextToken);
                return { endpoint: config.endpoint, region: config.region, decks };
            }));
            const result = new Map();
            const failedRegions = [];
            for (const r of results) {
                if (r.status === 'fulfilled') {
                    result.set(r.value.endpoint, { region: r.value.region, decks: r.value.decks });
                }
                else {
                    failedRegions.push(r.reason?.message || String(r.reason));
                }
            }
            const totalDecks = Array.from(result.values()).reduce((sum, v) => sum + v.decks.length, 0);
            if (totalDecks === 0) {
                if (failedRegions.length > 0) {
                    throw new Error(`Failed to fetch Witness identity documents from all regions: ${failedRegions.join(', ')}`);
                }
                throw new Error('Failed to fetch Witness identity documents: no documents returned from any region');
            }
            return result;
        }
        // Default: fail-fast
        const endpointToDeckMaps = await Promise.all(this.clients.map(async (config) => {
            const decks = [];
            let nextToken = undefined;
            do {
                const resp = await config.client.send(new ListIdentityDocumentsCommand({ nextToken }));
                if (resp.identityDocuments) {
                    for (const doc of resp.identityDocuments) {
                        if (doc.document) {
                            decks.push(doc.document);
                        }
                    }
                }
                nextToken = resp.nextToken;
            } while (nextToken);
            const output = new Map();
            output.set(config.endpoint, { region: config.region, decks });
            return output;
        }));
        const result = endpointToDeckMaps.reduce((acc, cur) => {
            cur.forEach((value, endpoint) => acc.set(endpoint, value));
            return acc;
        }, new Map());
        const totalDecks = Array.from(result.values()).reduce((sum, v) => sum + v.decks.length, 0);
        if (totalDecks === 0) {
            throw new Error('Failed to fetch Witness identity documents: no documents returned from any region');
        }
        return result;
    }
    createClient(authType, region, endpoint, credentials) {
        if (authType === "iam") {
            return new PiezoWitnessClient({
                endpoint,
                region,
                ...(credentials && { credentials }),
            });
        }
        else if (authType === "midway") {
            return new PiezoWitnessClient({
                endpoint,
                region: 'ignored',
                credentials: { accessKeyId: 'ignored', secretAccessKey: 'ignored' },
                requestHandler: new SentryFetchHttpHandler()
            });
        }
        else {
            throw new Error(`Unknown/unsupported AuthType: ${authType}`);
        }
    }
}
